import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogContent, Typography } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const POLL_MS = 30000;

/**
 * Blocking popup shown instead of the whole app while the instance is
 * suspended. It cannot be dismissed; it re-checks
 * /setup/status (never blocked) and reloads the app once re-activated.
 */
const SuspendedDialog = ({ reason }) => {
  const { t } = useTranslation();
  const [checking, setChecking] = useState(false);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      const { data } = await api.get('/setup/status');
      if (!data.suspended) {
        window.location.reload();
        return;
      }
    } catch {
      // backend unreachable — stay blocked
    }
    setChecking(false);
  }, []);

  useEffect(() => {
    const id = setInterval(check, POLL_MS);
    return () => clearInterval(id);
  }, [check]);

  return (
    <Dialog
      open
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.85)' } }}
    >
      <DialogContent sx={{ textAlign: 'center', py: 5 }}>
        <BlockIcon color="error" sx={{ fontSize: 56, mb: 2 }} />
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {t('suspended.title')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 1.5 }}>
          {reason || t('suspended.defaultReason')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('suspended.hint')}
        </Typography>
        <Box>
          <Button variant="outlined" onClick={check} disabled={checking}>
            {checking ? t('suspended.checking') : t('suspended.retry')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SuspendedDialog;
